<template>
  <button
    v-if="!flipped"
    class="btn btn-custom"
    @click="flipped = !flipped"
  >
    <i class="bi bi-box-arrow-right" />
  </button>
  <ScaleLoader v-if="loading" />
  <div
    v-if="flipped"
    class="group"
  >
    <ExportButtons
      :csv="exportGridsToExcel"
      :pdf="exportGridsToPdf"
      :xlsx="exportGridsToExcel"
    >
      <slot>
        <button
          class="btn btn-custom"
          @click="flipped = !flipped"
        >
          <i class="bi bi-box-arrow-left" />
        </button>
      </slot>
    </ExportButtons>
  </div>
  <transition name="rotateY">
    <div
      v-if="!flipped && loaded"
      class="row"
    >
      <div :class="windowWidth > 1500 ? 'col-sm-4' : 'col-sm-6'">
        <Card
          :badges="2"
          :info-badge-description="[dateFrom, dateTo]"
          :info-badge-title="[$t('components.history.summary.from'), $t('components.history.summary.to')]"
          :status="$t('components.history.summary.dateRange')"
          icon="bi bi-calendar3-range"
        />
      </div>
      <div :class="windowWidth > 1500 ? 'col-sm-4' : 'col-sm-6'">
        <Card
          v-if="hasCustomName"
          :badges="2"
          :info-badge-description="[vehicle.registration, vehicle.vars.name]"
          :info-badge-title="[$t('components.history.summary.registration'), $t('components.history.summary.customName')]"
          :status="$t('components.history.summary.vehicle')"
          icon="bi bi-truck"
        />
        <Card
          v-else
          :badges="1"
          :info-badge-description="[vehicle.registration]"
          :info-badge-title="[$t('components.history.summary.registration')]"
          :status="$t('components.history.summary.vehicle')"
          icon="bi bi-truck"
        />
      </div>
      <div
        v-if="hasCan"
        :class="windowWidth > 1500 ? 'col-sm-4' : 'col-sm-6'"
      >
        <Card
          :badges="2"
          :info-badge-description="[store.map_distance + ' [km]', store.can_distance + ' [km]']"
          :info-badge-title="[$t('components.history.summary.fromMap'), $t('components.history.summary.fromCan')]"
          :status="$t('components.history.summary.distance')"
          icon="bi bi-bezier2"
        />
      </div>
      <div
        v-else
        :class="windowWidth > 1500 ? 'col-sm-4' : 'col-sm-6'"
      >
        <Card
          v-if="store.map_distance > 0"
          :badges="1"
          :info-badge-description="[store.map_distance + ' [km]']"
          :info-badge-title="[$t('components.history.summary.fromMap')]"
          :status="$t('components.history.summary.distance')"
          icon="bi bi-bezier2"
        />
      </div>
      <div
        v-if="hasCan"
        :class="windowWidth > 1500 ? 'col-sm-4' : 'col-sm-6'"
      >
        <Card
          :badges="2"
          :info-badge-description="[store.odometer_start + ' [km]', store.odometer_stop + ' [km]']"
          :info-badge-title="[$t('components.history.summary.departure'), $t('components.history.summary.arrival')]"
          :status="$t('components.history.summary.odometer')"
          icon="bi bi-signpost-2"
        />
      </div>
      <div
        v-if="hasCan"
        :class="windowWidth > 1500 ? 'col-sm-4' : 'col-sm-6'"
      >
        <Card
          :badges="3"
          :info-badge-description="[store.fuel_start + ' [L]', store.fuel_stop + ' [L]', store.fuel_used + ' [L]']"
          :info-badge-title="[$t('components.history.summary.departure'), $t('components.history.summary.arrival'), $t('components.history.summary.burned')]"
          :status="$t('components.history.summary.fuel')"
          icon="bi bi-droplet-half"
        />
      </div>
      <div
        v-if="hasCan"
        :class="windowWidth > 1500 ? 'col-sm-4' : 'col-sm-6'"
      >
        <Card
          :badges="2"
          :info-badge-description="[store.fuel_rate_map + ' [L/100]', store.fuel_rate_can + ' [L/100]']"
          :info-badge-title="[$t('components.history.summary.byDistanceFromMap'), $t('components.history.summary.byDistanceFromCan')]"
          :status="$t('components.history.summary.averageFuelRate')"
          icon="bi bi-graph-down"
        />
      </div>
      <div :class="windowWidth > 1500 || delegationCountries.length > 3 ? 'col-sm-12' : 'col-sm-6'">
        <Card
          :badges="delegationCountries.length"
          :info-badge-description="delegationTimes"
          :info-badge-title="delegationCountries"
          :status="$t('components.history.summary.delegation')"
          icon="bi bi-globe"
        />
      </div>
    </div>
    <div
      v-else-if="flipped && loaded"
      class="row"
    >
      <ScaleLoader v-if="loading" />
      <DxDataGrid
        v-else
        :ref="dataGridRef[0]"
        :allow-column-reordering="true"
        :allow-column-resizing="true"
        :column-auto-width="true"
        :column-hiding-enabled="true"
        :data-source="store.border_crossing"
        :hover-state-enabled="true"
        :remote-operations="remoteOperations"
        :row-alternation-enabled="true"
        :show-borders="true"
        :show-column-lines="true"
        :show-row-lines="false"
      >
        <DxHeaderFilter :visible="true" />
        <DxPaging
          :page-index="0"
          :page-size="25"
        /> <!-- Shows the second page -->
        <DxPager
          :allowed-page-sizes="[10, 20, 25]"
          :show-info="true"
          :show-navigation-buttons="true"
          :show-page-size-selector="true"
        />
        <DxFilterRow :visible="true" />
        <DxColumnFixing :enabled="true" />
        <DxColumn :caption="$t('components.history.summary.dataGrids.borderCrossing')">
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.history.summary.dataGrids.fromCountry')"
            :filter-operations="allowedOperations"
            data-field="from_country"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.history.summary.dataGrids.toCountry')"
            :filter-operations="allowedOperations"
            data-field="to_country"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.history.summary.dataGrids.date')"
            :filter-operations="allowedOperations"
            data-field="timestamp"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.history.summary.dataGrids.distance')"
            :filter-operations="allowedOperations"
            data-field="distance"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.history.summary.dataGrids.place')"
            :filter-operations="allowedOperations"
            data-field="place"
          />
        </DxColumn>
      </DxDataGrid>

      <DxDataGrid
        :ref="dataGridRef[1]"
        :data-source="store.delegation_by_country"
        :remote-operations="remoteOperations"
        :row-alternation-enabled="true"
        :show-borders="true"
        :show-column-lines="true"
        :show-row-lines="false"
        style="margin-bottom: 0;"
      >
        <DxHeaderFilter :visible="true" />
        <DxPaging
          :page-index="0"
          :page-size="25"
        /> <!-- Shows the second page -->
        <DxPager
          :allowed-page-sizes="[10, 20, 25]"
          :show-info="true"
          :show-navigation-buttons="true"
          :show-page-size-selector="true"
        />
        <DxFilterRow :visible="true" />
        <DxColumnFixing :enabled="true" />
        <DxColumn :caption="$t('components.history.summary.dataGrids.workingTimeByCountry')">
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.history.summary.dataGrids.country')"
            :filter-operations="allowedOperations"
            data-field="country"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.history.summary.dataGrids.arrival')"
            :filter-operations="allowedOperations"
            data-field="arrival_timestamp"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.history.summary.dataGrids.departure')"
            :filter-operations="allowedOperations"
            data-field="departure_timestamp"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.history.summary.dataGrids.timeOfVisit')"
            :filter-operations="allowedOperations"
            data-field="time"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.history.summary.dataGrids.driving')"
            :filter-operations="allowedOperations"
            data-field="drive_time"
            width="150"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.history.summary.dataGrids.otherWork')"
            :filter-operations="allowedOperations"
            data-field="work_time"
            width="150"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.history.summary.dataGrids.breakAndRest')"
            :filter-operations="allowedOperations"
            data-field="rest_time"
            width="150"
          />
        </DxColumn>
      </DxDataGrid>
      <h5 style="text-align: right; padding: 10px; margin: 0; background: #1e1e2d; color: white;">
        {{ $t('components.history.summary.dataGrids.sum') }}
      </h5>
      <DxDataGrid
        :ref="dataGridRef[2]"
        :data-source="store.delegation_by_country_sum"
        :row-alternation-enabled="true"
        :show-borders="true"
        :show-column-headers="false"
        :show-column-lines="true"
        :show-row-lines="false"
      >
        <DxColumn
          :allow-filtering="true"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          data-field="country"
        />
        <DxColumn
          :allow-filtering="true"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          data-field="drive_time"
          width="150"
        />
        <DxColumn
          :allow-filtering="true"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          data-field="work_time"
          width="150"
        />
        <DxColumn
          :allow-filtering="true"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
          data-field="rest_time"
          width="150"
        />
      </DxDataGrid>

      <DxDataGrid
        :ref="dataGridRef[3]"
        :allow-column-reordering="true"
        :allow-column-resizing="true"
        :column-auto-width="true"
        :column-hiding-enabled="true"
        :data-source="store.gps_signal_loss"
        :hover-state-enabled="true"
        :remote-operations="remoteOperations"
        :row-alternation-enabled="true"
        :show-borders="true"
        :show-column-lines="true"
        :show-row-lines="false"
      >
        <DxHeaderFilter :visible="true" />
        <DxPaging
          :page-index="0"
          :page-size="25"
        /> <!-- Shows the second page -->
        <DxPager
          :allowed-page-sizes="[10, 20, 25]"
          :show-info="true"
          :show-navigation-buttons="true"
          :show-page-size-selector="true"
        />
        <DxFilterRow :visible="true" />
        <DxColumnFixing :enabled="true" />
        <DxColumn :caption="$t('components.history.summary.dataGrids.gpsSignalLoss')">
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.history.summary.dataGrids.from')"
            :filter-operations="allowedOperations"
            data-field="from"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.history.summary.dataGrids.to')"
            :filter-operations="allowedOperations"
            data-field="to"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.history.summary.dataGrids.duration')"
            :filter-operations="allowedOperations"
            data-field="diff"
          />
        </DxColumn>
      </DxDataGrid>
    </div>
  </transition>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import Card from '@/components/extras/Card.vue';
import { v4 as uuidv4 } from 'uuid';
import { getHistorySummary, getReverseGeocode } from '@/crud/getters';
import { useWindowSize } from 'vue-window-size';
import { getHumanDateTime, minutesToHoursMinutes, secondsToHoursMinutes } from '@/utils/tools';
import {
  DxColumn,
  DxColumnFixing,
  DxDataGrid,
  DxFilterRow,
  DxHeaderFilter,
  DxPager,
  DxPaging
} from 'devextreme-vue/data-grid';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import ExportButtons from '@/components/content/_partials/ExportButtons.vue';
import { jsPDF } from 'jspdf';
import { exportDataGrid as exportDataGridToPdf } from 'devextreme/pdf_exporter';
import { importFont } from '@/utils/jspdf/importFont';
import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue';

export default defineComponent({
  name: 'Summary',
  components: {
    ExportButtons,
    Card,
    DxColumn,
    DxColumnFixing,
    DxDataGrid,
    DxFilterRow,
    DxHeaderFilter,
    DxPager,
    DxPaging,
    ScaleLoader
  },
  setup() {
    const { width } = useWindowSize();
    return { windowWidth: width };
  },
  props: {
    imei: BigInt as unknown as PropType<bigint>,
    dateFrom: String,
    dateTo: String,
    vehicle: Object
  },
  emits: ['summary-data-loaded'],
  data() {
    return {
      store: {} as Record<string, any>,
      hasCan: false,
      hasCustomName: false,
      delegationCountries: [] as Array<any>,
      delegationTimes: [] as Array<any>,
      loaded: false,
      flipped: false,
      allowedOperations: ['contains', '=', 'notcontains', '!='],
      remoteOperations: true,
      dataGridRef: ['border_crossing', 'delegation_by_country', 'delegation_by_country_sum', 'gps_signal_loss'],
      loading: true
    }
  },
  computed: {
    borderCrossingGridInstance() {
      const self: any = this
      return self.$refs[this.dataGridRef[0]].instance
    },
    delegationByCountryGridInstance() {
      const self: any = this
      return self.$refs[this.dataGridRef[1]].instance
    },
    delegationByCountrySumGridInstance() {
      const self: any = this
      return self.$refs[this.dataGridRef[2]].instance
    },
    gpsSignalLossGridInstance() {
      const self: any = this
      return self.$refs[this.dataGridRef[3]].instance
    }
  },
  methods: {
    delay(ms: number) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    async performReverseGeocodingOnBorderCrossing() {
      if (this.store.border_crossing && this.store.border_crossing.length > 0) {
        const reverseGeocodePromises = this.store.border_crossing.map((obj) => {
          if (obj.place && this.isCoordinate(obj.place)) {
            return getReverseGeocode(obj.place)
                .then((response) => {
                  obj.place = response.data;
                })
                .catch((error) => {
                  console.error('Reverse geocoding error for place:', obj.place, error);
                });
          }
          return Promise.resolve();
        });
        return Promise.all(reverseGeocodePromises);
      }
      return Promise.resolve();
    },
    isCoordinate(value: string) {
      const coordinatePattern = /^-?\d+(\.\d+)?,-?\d+(\.\d+)?$/;
      return coordinatePattern.test(value);
    },
    async exportGridsToExcel(extension: string) {
      const self: any = this;
      const randomId = uuidv4(); // Generate UUID for unique filename

      const exportGrid = (component, worksheetName) => {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet(worksheetName);

        return exportDataGrid({
          worksheet: worksheet,
          component: component
        }).then(() => {
          if (extension == 'csv') {
            return workbook.csv.writeBuffer().then((buffer) => {
              saveAs(new Blob([buffer], {type: 'text/csv'}),
                  `${worksheetName}_${self.imei}_${randomId}.csv`);
            });
          } else if (extension == 'xlsx') {
            return workbook.xlsx.writeBuffer().then((buffer) => {
              saveAs(new Blob([buffer], {type: 'application/octet-stream'}),
                  `Summary_${self.imei}_${randomId}.xlsx`);
            });
          }
        });
      };

      if (extension == 'csv') {
        // Export each grid to its own CSV file
        Promise.all([
          exportGrid(self.borderCrossingGridInstance, self.$t('components.history.summary.dataGrids.borderCrossing')),
          exportGrid(self.delegationByCountryGridInstance, self.$t('components.history.summary.dataGrids.workingTimeByCountry')),
          exportGrid(self.delegationByCountrySumGridInstance, self.$t('components.history.summary.dataGrids.sum')),
          exportGrid(self.gpsSignalLossGridInstance, self.$t('components.history.summary.dataGrids.gpsSignalLoss'))
        ]).then(() => {
          // All CSV exports completed
        }).catch((error) => {
          console.error("CSV export error:", error);
        });
      } else if (extension == 'xlsx') {
        // For XLSX, export all grids into one workbook with multiple sheets
        const workbook = new Workbook();

        exportDataGrid({
          worksheet: workbook.addWorksheet(self.$t('components.history.summary.dataGrids.borderCrossing')),
          component: self.borderCrossingGridInstance
        })
            .then(() => exportDataGrid({
              worksheet: workbook.addWorksheet(self.$t('components.history.summary.dataGrids.workingTimeByCountry')),
              component: self.delegationByCountryGridInstance
            }))
            .then(() => exportDataGrid({
              worksheet: workbook.addWorksheet(self.$t('components.history.summary.dataGrids.sum')),
              component: self.delegationByCountrySumGridInstance
            }))
            .then(() => exportDataGrid({
              worksheet: workbook.addWorksheet(self.$t('components.history.summary.dataGrids.gpsSignalLoss')),
              component: self.gpsSignalLossGridInstance
            }))
            .then(() => {
              workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }),
                    `Summary_${self.imei}_${randomId}.xlsx`);
              });
            })
            .catch((error) => {
              console.error("XLSX export error:", error);
            });
      }
    },
    async exportGridsToPdf() {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      importFont(doc);

      const margin = 10; // Left and right margin in mm

      const exportGridToPdf = (component, title, addPage) => {
        return new Promise<void>((resolve) => {
          if (addPage) {
            doc.addPage();
          }

          const currentY = margin; // Starting Y position for each grid

          // Add a title for each grid
          doc.setFontSize(16);
          doc.text(title, margin, currentY);
          const contentY = currentY + 10; // Adjust for the title height

          // Get the total page width in mm
          const pageWidth = doc.internal.pageSize.getWidth();
          const availablePageWidth = pageWidth - (2 * margin);

          // Get the visible columns from the grid instance
          const visibleColumns = component.getVisibleColumns();
          const numberOfColumns = visibleColumns.length;

          // Calculate column widths to fit within the available page width
          const columnWidth = availablePageWidth / numberOfColumns;
          const columnWidths = new Array(numberOfColumns).fill(columnWidth);

          exportDataGridToPdf({
            jsPDFDocument: doc,
            component: component,
            topLeft: {x: margin, y: contentY},
            repeatHeaders: true,
            margin: {top: 0},
            columnWidths: columnWidths, // Set the calculated column widths
          }).then(() => {
            resolve();
          }).catch(error => {
            console.error("Error exporting grid:", error);
            resolve(); // Resolve even if there's an error to continue with the next grid
          });
        });
      };

      const randomId = uuidv4(); // Generate UUID for unique filename

      exportGridToPdf(this.borderCrossingGridInstance, this.$t('components.history.summary.dataGrids.borderCrossing'), false)
          .then(() => exportGridToPdf(this.delegationByCountryGridInstance, this.$t('components.history.summary.dataGrids.workingTimeByCountry'), true))
          .then(() => exportGridToPdf(this.delegationByCountrySumGridInstance, this.$t('components.history.summary.dataGrids.sum'), true))
          .then(() => exportGridToPdf(this.gpsSignalLossGridInstance, this.$t('components.history.summary.dataGrids.gpsSignalLoss'), true))
          .then(() => {
            doc.save(`Summary_${this.imei}_${randomId}.pdf`);
          })
          .catch(error => {
            console.error("PDF generation error:", error);
          });
    }
  },
  async mounted() {
    try {
      if (this.imei && this.dateFrom && this.dateTo) {
        const response = await getHistorySummary(this.imei, this.dateFrom, this.dateTo);
        this.store = response.data;
        
        // Emit dane do komponentu rodzica (History.vue)
        this.$emit('summary-data-loaded', this.store);

        this.hasCan = this.store.fuel_start >= 0 && this.store.fuel_stop >= 0;
        this.hasCustomName = this.vehicle?.vars?.name?.length > 0;

        if (Array.isArray(this.store.border_crossing)) {
          this.store.border_crossing.forEach((obj: any) => {
            obj.from_country = this.$t(`components.history.summary.countries.${obj.from_country.toLowerCase()}`);
            obj.to_country = this.$t(`components.history.summary.countries.${obj.to_country.toLowerCase()}`);
            obj.timestamp = getHumanDateTime(obj.timestamp);
          });
        } else {
          console.warn('store.border_crossing is not an array:', this.store.border_crossing);
        }

        if (Array.isArray(this.store.delegation_by_country)) {
          this.store.delegation_by_country.forEach((obj: any) => {
            obj.country = this.$t(`components.history.summary.countries.${obj.country.toLowerCase()}`);

            if (obj.arrival_timestamp) {
              obj.arrival_timestamp = getHumanDateTime(obj.arrival_timestamp);
            }
            if (obj.departure_timestamp) {
              obj.departure_timestamp = getHumanDateTime(obj.departure_timestamp);
            }

            if (obj.time) {
              obj.time = secondsToHoursMinutes(obj.time);
            }

            if (obj.drive_time !== undefined) {
              obj.drive_time = minutesToHoursMinutes(obj.drive_time);
            }
            if (obj.work_time !== undefined) {
              obj.work_time = minutesToHoursMinutes(obj.work_time);
            }
            if (obj.rest_time !== undefined) {
              obj.rest_time = minutesToHoursMinutes(obj.rest_time);
            }
          });
        } else {
          console.warn('store.delegation_by_country is not an array:', this.store.delegation_by_country);
        }

        if (Array.isArray(this.store.delegation_by_country_sum)) {
          this.store.delegation_by_country_sum.forEach((obj: any) => {
            obj.country = this.$t(`components.history.summary.countries.${obj.country.toLowerCase()}`);
            if (obj.drive_time !== undefined) {
              obj.drive_time = minutesToHoursMinutes(obj.drive_time);
            }
            if (obj.work_time !== undefined) {
              obj.work_time = minutesToHoursMinutes(obj.work_time);
            }
            if (obj.rest_time !== undefined) {
              obj.rest_time = minutesToHoursMinutes(obj.rest_time);
            }
          });
        } else {
          console.warn('store.delegation_by_country_sum is not an array:', this.store.delegation_by_country_sum);
        }

        if (Array.isArray(this.store.gps_signal_loss)) {
          this.store.gps_signal_loss.forEach((obj: any) => {
            if (obj.from) {
              obj.from = getHumanDateTime(obj.from);
            }
            if (obj.to) {
              obj.to = getHumanDateTime(obj.to);
            }
            if (obj.diff !== undefined) {
              obj.diff = secondsToHoursMinutes(obj.diff);
            }
          });
        } else {
          console.warn('store.gps_signal_loss is not an array:', this.store.gps_signal_loss);
        }

        await this.performReverseGeocodingOnBorderCrossing();

        this.loaded = true;
      }
    } catch (error) {
      console.error('Error fetching history summary:', error);
    } finally {
      this.loading = false;
    }
  }
});
</script>

<style lang="scss" scoped>
.rotateY-enter-active, .rotateY-leave-active {
  transition: transform 0.5s, opacity 0.4s;
}

.rotateY-enter-from, .rotateY-leave-to {
  transform: rotateY(180deg);
  opacity: 0;
}

.btn-custom {
  display: block;
  margin: 0 0 0 auto;
}

.dx-widget {
  margin-bottom: 35px;
}

button {
  i {
    font-size: 2rem;
  }
}

.group {
  text-align: right;

  button {
    display: inline-block;
  }
}
</style>